import { ChevronRight } from 'lucide-react';
import { Badge } from '@/shared/components/Badge';
import { getLogLevelMeta } from '@/features/admin/lib/logLevelMeta';
import { formatLogTimestamp } from '@/shared/utils/date';
import { cn } from '@/shared/utils/cn';
import type { ServerLogEntry } from '@/shared/types/api';
import { LogEntryDetail } from './LogEntryDetail';

const COLUMN_COUNT = 5;

/** "FenergoRss.Infrastructure.Services.RssProcessingService" → "RssProcessingService". */
const shortSource = (source?: string | null) => (source ? source.slice(source.lastIndexOf('.') + 1) : '—');

interface LogRowProps {
  entry: ServerLogEntry;
  expanded: boolean;
  onToggle: () => void;
  onShowTrace: (traceId: string, timestamp?: string) => void;
}

export function LogRow({ entry, expanded, onToggle, onShowTrace }: LogRowProps) {
  const level = getLogLevelMeta(entry.level);
  const LevelIcon = level.icon;
  const detailId = `log-detail-${entry.id}`;

  return (
    <>
      <tr className={cn('align-top hover:bg-muted/50', expanded && 'bg-muted/30')}>
        <td className="w-8 py-2 pl-2 pr-0">
          <button
            type="button"
            className="btn-icon h-7 w-7"
            aria-expanded={expanded}
            aria-controls={expanded ? detailId : undefined}
            aria-label={expanded ? 'Collapse log entry' : 'Expand log entry'}
            onClick={onToggle}
          >
            <ChevronRight className={cn('h-4 w-4 transition-transform', expanded && 'rotate-90')} />
          </button>
        </td>
        <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-muted-foreground" title={entry.timestamp}>
          {formatLogTimestamp(entry.timestamp)}
        </td>
        <td className="whitespace-nowrap px-3 py-2">
          <Badge variant={level.badge}>
            <LevelIcon className="h-3 w-3" aria-hidden />
            {level.label}
          </Badge>
        </td>
        <td className="max-w-48 truncate whitespace-nowrap px-3 py-2 font-mono text-xs text-muted-foreground" title={entry.sourceContext ?? undefined}>
          {shortSource(entry.sourceContext)}
        </td>
        <td className="px-3 py-2">
          <p className="line-clamp-2 break-words font-mono text-sm text-foreground">{entry.message}</p>
          {entry.exceptionType && <p className="mt-0.5 truncate font-mono text-xs text-danger">{entry.exceptionType}</p>}
        </td>
      </tr>
      {expanded && (
        <tr id={detailId} className="bg-muted/30">
          <td colSpan={COLUMN_COUNT} className="px-4 pb-4 pt-1 sm:pl-12">
            <LogEntryDetail entry={entry} onShowTrace={onShowTrace} />
          </td>
        </tr>
      )}
    </>
  );
}
